/**
 * UTM link block inside PlacementDetailModal.
 *
 * Lets the user set a target URL + placement type, see the auto-generated
 * UTM link, and optionally edit individual parameters before saving.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronUp, Copy, RefreshCw } from "lucide-react";
import {
  type Blogger,
  type Placement,
  type UtmPreview,
  usePatchPlacement,
  useUtmPreview,
} from "@/shared/api/wb";

const PLACEMENT_TYPES = [
  { value: "native",  label: "Нативная интеграция" },
  { value: "seeding", label: "Посев" },
  { value: "banner",  label: "Баннер / Stories" },
  { value: "repost",  label: "Репост" },
  { value: "review",  label: "Обзор / review" },
  { value: "other",   label: "Прочее" },
] as const;

type Props = {
  placement: Placement;
  campaignId: string;
  blogger: Blogger | undefined;
};

export function UtmBlock({ placement, campaignId, blogger }: Props) {
  const [targetUrl, setTargetUrl]       = useState(placement.target_url ?? "");
  const [placementType, setPlacementType] = useState(placement.placement_type ?? "native");
  const [preview, setPreview]           = useState<UtmPreview | null>(
    placement.utm_link
      ? {
          utm_link: placement.utm_link,
          utm_source: placement.utm_source ?? "",
          utm_medium: placement.utm_medium ?? "",
          utm_campaign: placement.utm_campaign ?? "",
          utm_content: placement.utm_content ?? "",
          utm_term: placement.utm_term ?? null,
          has_existing_utm: false,
        }
      : null,
  );
  const [custom, setCustom]             = useState<Partial<UtmPreview> | null>(null);
  const [showParams, setShowParams]     = useState(false);
  const [urlError, setUrlError]         = useState("");
  const [copied, setCopied]             = useState(false);
  const [saved, setSaved]               = useState(false);

  const previewMutation  = useUtmPreview(campaignId, placement.id);
  const patchMutation    = usePatchPlacement(campaignId);
  const debounceRef      = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isValidUrl = (s: string) => {
    try { const u = new URL(s); return u.protocol === "https:" || u.protocol === "http:"; }
    catch { return false; }
  };

  useEffect(() => {
    if (!targetUrl) { setUrlError(""); return; }
    if (!isValidUrl(targetUrl)) { setUrlError("Введите корректный URL начиная с https://"); return; }
    setUrlError("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      previewMutation.mutate(
        { targetUrl, placementType },
        {
          onSuccess: (data) => { setPreview(data); setCustom(null); },
          onError: () => setUrlError("Не удалось сформировать UTM-ссылку"),
        },
      );
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUrl, placementType]);

  const effective = custom ? { ...preview, ...custom } as UtmPreview : preview;

  const rebuildLink = (params: UtmPreview) => {
    try {
      const url = new URL(params.utm_link);
      // strip existing utm_* from whatever link we have
      const base = new URL(targetUrl || params.utm_link);
      base.searchParams.forEach((_v, k) => { if (k.startsWith("utm_")) base.searchParams.delete(k); });
      base.searchParams.set("utm_source",   params.utm_source);
      base.searchParams.set("utm_medium",   params.utm_medium);
      base.searchParams.set("utm_campaign", params.utm_campaign);
      base.searchParams.set("utm_content",  params.utm_content);
      if (params.utm_term) base.searchParams.set("utm_term", params.utm_term);
      return base.toString();
    } catch { return params.utm_link; }
  };

  const handleParamChange = (key: keyof UtmPreview, value: string) => {
    if (!effective) return;
    const next = { ...effective, [key]: value || null } as UtmPreview;
    next.utm_link = rebuildLink(next);
    setCustom(next);
  };

  const handleReset = () => {
    setCustom(null);
    if (targetUrl && isValidUrl(targetUrl)) {
      previewMutation.mutate({ targetUrl, placementType }, {
        onSuccess: (data) => setPreview(data),
      });
    }
  };

  const handleCopy = () => {
    if (!effective?.utm_link) return;
    navigator.clipboard.writeText(effective.utm_link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSave = () => {
    const patch = {
      target_url: targetUrl || null,
      placement_type: placementType,
      ...(custom
        ? {
            utm_source:   custom.utm_source ?? effective?.utm_source,
            utm_medium:   custom.utm_medium ?? effective?.utm_medium,
            utm_campaign: custom.utm_campaign ?? effective?.utm_campaign,
            utm_content:  custom.utm_content ?? effective?.utm_content,
            utm_term:     custom.utm_term ?? effective?.utm_term ?? null,
            utm_is_custom: true,
          }
        : {}),
    };
    patchMutation.mutate(
      { placementId: placement.id, patch },
      {
        onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); },
      },
    );
  };

  return (
    <section className="grid gap-3">
      <div className="text-[11px] font-display font-bold uppercase tracking-[0.08em] text-ink-40">
        Ссылка для размещения
      </div>

      <div className="grid gap-2 p-4 bg-white rounded-md border border-ink-12">
        {/* Source info */}
        {blogger && (
          <div className="text-[11px] text-ink-40 flex gap-4">
            <span>Площадка: <span className="font-semibold text-ink-60">{blogger.platform}</span></span>
          </div>
        )}

        {/* Target URL */}
        <label className="grid gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-40 font-display font-semibold">
            Целевой URL
          </span>
          <input
            type="url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://brand.ru/sale"
            className="h-9 px-3 rounded-md border border-ink-12 text-[13px] focus:outline-none focus:border-ink-40 bg-[var(--bg)] w-full"
          />
          {urlError && (
            <span className="text-[11.5px] text-danger flex items-center gap-1">
              <AlertTriangle size={11} /> {urlError}
            </span>
          )}
        </label>

        {/* Placement type */}
        <label className="grid gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-40 font-display font-semibold">
            Тип размещения
          </span>
          <select
            value={placementType}
            onChange={(e) => setPlacementType(e.target.value)}
            className="h-9 px-3 rounded-md border border-ink-12 text-[13px] focus:outline-none focus:border-ink-40 bg-[var(--bg)] w-full"
          >
            {PLACEMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        {/* Generated link */}
        {effective && !urlError && (
          <>
            {effective.has_existing_utm && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-warning/10 text-warning text-[12px]">
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                В ссылке уже есть UTM-метки. Рекомендуем использовать автоматически сгенерированную ссылку.
              </div>
            )}

            <label className="grid gap-1">
              <span className="text-[11px] uppercase tracking-wider text-ink-40 font-display font-semibold">
                Размеченная ссылка
              </span>
              <div className="flex gap-1.5">
                <input
                  readOnly
                  value={effective.utm_link}
                  className="h-9 px-3 rounded-md border border-ink-12 text-[12px] bg-[var(--bg-secondary,#f5f5f5)] flex-1 min-w-0 text-ink-60 font-mono select-all"
                />
                <button
                  onClick={handleCopy}
                  className="h-9 w-9 flex-shrink-0 grid place-items-center rounded-md border border-ink-12 hover:bg-ink-6"
                  title="Копировать"
                >
                  {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                </button>
              </div>
            </label>

            {/* Toggle UTM params */}
            <button
              onClick={() => setShowParams((v) => !v)}
              className="flex items-center gap-1.5 text-[12px] font-display font-semibold text-ink-40 hover:text-ink w-fit"
            >
              {showParams ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              UTM-параметры
            </button>

            {showParams && (
              <div className="grid gap-2 pt-1 border-t border-ink-12">
                {(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const).map((key) => (
                  <label key={key} className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="text-[11px] text-ink-40 font-mono">{key}</span>
                    <input
                      type="text"
                      value={
                        custom && key in custom
                          ? (custom as unknown as Record<string, string | null>)[key] ?? ""
                          : (effective as unknown as Record<string, string | null>)[key] ?? ""
                      }
                      onChange={(e) => handleParamChange(key, e.target.value)}
                      className="h-8 px-2 rounded border border-ink-12 text-[12px] focus:outline-none focus:border-ink-40 font-mono"
                    />
                  </label>
                ))}

                {custom && (
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-1.5 text-[12px] text-ink-40 hover:text-ink w-fit mt-1"
                  >
                    <RefreshCw size={12} /> Сбросить к автоматическим
                  </button>
                )}
              </div>
            )}

            {/* Save */}
            <div className="flex justify-end pt-1">
              <button
                onClick={handleSave}
                disabled={patchMutation.isPending}
                className="inline-flex items-center gap-1.5 h-8 px-4 rounded-pill bg-ink text-white text-[12px] font-display font-semibold hover:bg-[#2a2a2a] disabled:opacity-50"
              >
                {saved ? (
                  <><Check size={13} /> Сохранено</>
                ) : patchMutation.isPending ? (
                  "Сохранение…"
                ) : (
                  "Сохранить ссылку"
                )}
              </button>
            </div>
          </>
        )}

        {!targetUrl && (
          <p className="text-[12.5px] text-ink-40">
            Введите целевой URL — система автоматически сформирует размеченную ссылку.
          </p>
        )}
      </div>
    </section>
  );
}
